import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail, CommonUtils } from '../../utils/commonUtils';
import { ProductFormUtils } from '../../utils/productCommon';
import { getProductDetail, type ProductDetailResponse } from '../../utils/product';
import { getBrandListFlat, type FlatBrandNode } from '../../utils/brand';
import {
  getProductToGroupMappings,
  replaceProductToGroupMappings,
} from '../../utils/productOptionGroup';
import { generateAfter } from '../../utils/fractionalIndex';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import type { ProductMappedOptionGroupLine } from './components/productOptionGroupFormTypes';
import ProductOptionGroupConfigProductSummary from './components/ProductOptionGroupConfigProductSummary';
import ProductMappedOptionGroupsTab from './components/ProductMappedOptionGroupsTab';

function recomputeSortOrders(lines: ProductMappedOptionGroupLine[]): ProductMappedOptionGroupLine[] {
  let prev: string | null = null;
  return lines.map((line) => {
    const sortOrder = generateAfter(prev);
    prev = sortOrder;
    return { ...line, sortOrder };
  });
}

const ProductOptionGroupConfig: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { productCode: productCodeParam } = useParams<{ productCode: string }>();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('product-list');

  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);
  const [productDetail, setProductDetail] = useState<ProductDetailResponse | null>(null);
  const [lines, setLines] = useState<ProductMappedOptionGroupLine[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchSeq = useRef(0);

  ProductFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    const p = ProductFormUtils.loadProjectDetail();
    if (p) setProjectDetail(p);
  }, []);

  useEffect(() => {
    if (!projectDetail) return;
    const pc = projectDetail.code || getProjectCode();
    if (!pc) return;
    let cancelled = false;
    getBrandListFlat(pc)
      .then((res) => {
        if (!cancelled && res?.brands) setBrands(res.brands);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectDetail?.code]);

  const projectCode = projectDetail?.code || getProjectCode() || '';

  useEffect(() => {
    const run = async () => {
      if (!projectDetail || !productCodeParam || !projectCode) return;
      const seq = ++fetchSeq.current;
      setIsLoading(true);
      setError(null);
      const decodedCode = decodeURIComponent(productCodeParam);
      try {
        const product = await getProductDetail(projectCode, decodedCode);
        if (seq !== fetchSeq.current) return;
        setProductDetail(product);
        const items = await getProductToGroupMappings(projectCode, decodedCode);
        if (seq !== fetchSeq.current) return;
        const mapped: ProductMappedOptionGroupLine[] = items
          .filter((it) => it.groupCode)
          .sort((a, b) => String(a.sortOrder).localeCompare(String(b.sortOrder)))
          .map((it) => ({
            groupCode: it.groupCode,
            groupName: it.groupName || it.groupCode,
            sortOrder: it.sortOrder || '',
            status: it.status,
            itemProductCount: it.itemProductCount,
            mappingProductCount: it.mappingProductCount,
          }));
        const needsRecompute =
          mapped.length > 0 && mapped.some((l) => !String(l.sortOrder || '').trim());
        setLines(needsRecompute ? recomputeSortOrders(mapped) : mapped);
      } catch (e) {
        if (seq !== fetchSeq.current) return;
        setError(CommonUtils.getNetworkErrorMessage(e));
        setProductDetail(null);
        setLines([]);
      } finally {
        if (seq === fetchSeq.current) setIsLoading(false);
      }
    };
    run();
  }, [projectDetail, projectCode, productCodeParam]);

  const goToProductList = () => {
    const params = new URLSearchParams(location.search);
    const page = params.get('page');
    const size = params.get('size');
    const q = new URLSearchParams();
    if (page) q.set('page', page);
    if (size) q.set('size', size);
    navigate(`/products${q.toString() ? `?${q.toString()}` : ''}`);
  };

  const handleSave = async () => {
    if (!projectCode || !productCodeParam) return;
    setError(null);
    setIsSaving(true);
    try {
      const decodedCode = decodeURIComponent(productCodeParam);
      const result = await replaceProductToGroupMappings(projectCode, decodedCode, {
        items: lines.map((l) => ({
          groupCode: l.groupCode,
          defaultSelect: 0,
          defaultQuantity: 0,
          maxQuantity: 0,
          minQuantity: 0,
          sortOrder: l.sortOrder,
        })),
      });
      if (!result.success) {
        setError(result.message || '저장에 실패했습니다.');
        return;
      }
      alert(result.message || '저장되었습니다.');
    } catch (e) {
      setError(CommonUtils.getNetworkErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  };

  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-screen bg-gray-50 flex overflow-hidden">
        <ProjectSidebar
          isDesktopSidebarOpen={isDesktopSidebarOpen}
          setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          activeMenu={activeMenu}
          setActiveMenu={setActiveMenu}
          projectDetail={projectDetail}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="상품 옵션 그룹 설정"
          subtitle={projectDetail.name}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo
          projectDetail={projectDetail}
        />
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto space-y-6">
            <div>
              <button
                type="button"
                onClick={goToProductList}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                상품 목록으로
              </button>
            </div>

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {productDetail && (
              <ProductOptionGroupConfigProductSummary product={productDetail} brands={brands} />
            )}

            {productDetail && (
              <ProductMappedOptionGroupsTab
                projectCode={projectCode}
                brandCode={productDetail.brandCode ?? ''}
                brandName={brands.find((b) => b.code === productDetail.brandCode)?.name}
                lines={lines}
                onLinesChange={setLines}
              />
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={goToProductList}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                목록으로
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || !productDetail}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {isSaving ? '저장 중...' : '옵션 그룹 매핑 저장'}
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProductOptionGroupConfig;
